/**
 * 端末の方位センサー（DeviceOrientationEvent）のラッパー。
 * geolocation.ts と同じ「停止関数を返す」流儀。
 */

export type CompassSupport = 'none' | 'ios-permission-required' | 'available';

interface RequestPermissionOrientationEvent {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

function requestPermissionFn(): (() => Promise<'granted' | 'denied'>) | undefined {
  if (typeof DeviceOrientationEvent === 'undefined') return undefined;
  return (DeviceOrientationEvent as unknown as RequestPermissionOrientationEvent).requestPermission;
}

export function detectCompassSupport(): CompassSupport {
  if (typeof DeviceOrientationEvent === 'undefined') return 'none';
  return typeof requestPermissionFn() === 'function' ? 'ios-permission-required' : 'available';
}

/**
 * このページを読み込んでから、方位センサーの利用が一度でも許可された
 * ことがあるか。iOSは許可状態をブラウザが記憶してくれないため、
 * コンパス窓・パイバル計測のどちらから許可しても、もう一方を含めて
 * 「開くたびに許可を求める」ダイアログを出さずに済むよう、
 * このモジュール内で憶えておく（ページを再読み込みするとまた必要）。
 */
let permissionGranted = false;

export function hasCompassPermission(): boolean {
  return permissionGranted;
}

/**
 * iOSの方位センサー許可プロンプト。
 * ★ ユーザー操作（クリックハンドラ）の中で直接呼び出すこと。非同期処理を
 * 挟んでから呼ぶとiOSは許可プロンプトを出さない。
 * iOS以外（requestPermissionが無い環境）では常に'granted'を返す。
 */
export async function requestCompassPermission(): Promise<'granted' | 'denied'> {
  const fn = requestPermissionFn();
  if (typeof fn !== 'function') {
    permissionGranted = true;
    return 'granted';
  }
  try {
    const result = await fn();
    if (result === 'granted') permissionGranted = true;
    return result;
  } catch {
    return 'denied';
  }
}

export interface CompassReading {
  /** 磁北を基準にした方位 [0,360) */
  headingDeg: number;
  source: 'ios-webkit' | 'absolute-alpha' | 'relative-alpha';
}

interface OrientationLike {
  alpha: number | null;
  absolute?: boolean;
  webkitCompassHeading?: number;
}

/** イベント1件から方位を取り出す純粋関数（DOM無しでテストできるよう分離） */
export function readingFromOrientationEvent(e: OrientationLike): CompassReading | null {
  if (typeof e.webkitCompassHeading === 'number') {
    return { headingDeg: e.webkitCompassHeading, source: 'ios-webkit' };
  }
  if (e.alpha == null) return null;
  return {
    headingDeg: ((360 - e.alpha) % 360 + 360) % 360,
    source: e.absolute ? 'absolute-alpha' : 'relative-alpha',
  };
}

/**
 * 方位の継続購読を開始する。戻り値の関数を呼ぶと停止する。
 *
 * DeviceOrientationEventの無い環境では即座にエラーを通知し、no-opの
 * 停止関数を返す。
 */
export function subscribeCompassHeading(
  onReading: (r: CompassReading) => void,
  onError: (message: string) => void,
): () => void {
  if (typeof DeviceOrientationEvent === 'undefined') {
    onError('この端末・ブラウザは方位センサーに対応していません');
    return () => {};
  }
  const handler = (e: Event): void => {
    const r = readingFromOrientationEvent(e as unknown as OrientationLike);
    if (r) onReading(r);
  };
  const eventName = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
  window.addEventListener(eventName, handler);
  return () => window.removeEventListener(eventName, handler);
}
